import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { userIdentityQuery, userFreeTrialStatusQuery } from '../graphql-operations/UserIdentity.graphql';
import { isBundleCartItem } from '../lib/cart';
import * as types from '../types';
import { userIdentity } from '../types/gql-op-types';
import useHardwareFirstCheckout from './useHardwareFirstCheckout';

export type FreeTrialRestrictionReason = 'in_cart' | 'previously_purchased' | 'active_membership' | null;

interface FreeTrialEligibility {
  isEligible: boolean;
  restrictionReason: FreeTrialRestrictionReason;
}

interface UserFreeTrialStatus {
  currentUser?: {
    userHouseholds?: Array<{
      household?: {
        pets?: Array<{
          device?: {
            hasActiveSubscription?: boolean | null;
            subscription?: {
              hasFreeTrialPlan?: boolean | null;
            } | null;
          } | null;
        }>;
      };
    }>;
  };
}

/**
 * Checks if user is eligible for a free trial.
 * Returns ineligible if user has a free trial in cart or has an existing free trial subscription.
 * Hardware-first checkout is ineligible unless `includeHardwareFirst` is set, which runs the same
 * checks for the hardware-first trial.
 */
export default function useFreeTrialEligibility(
  collarProductId: string,
  { includeHardwareFirst = false }: { includeHardwareFirst?: boolean } = {},
): FreeTrialEligibility {
  const isHardwareFirstEnrolled = useHardwareFirstCheckout() && !includeHardwareFirst;
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);

  const { data: userData } = useQuery<userIdentity>(userIdentityQuery, {
    skip: isHardwareFirstEnrolled || !collarProductId,
  });

  const previouslyPurchasedCollarProductIds = useMemo(
    () => userData?.currentUser?.previouslyPurchasedCollarProductIds ?? [],
    [userData?.currentUser?.previouslyPurchasedCollarProductIds],
  );

  const { data: freeTrialData } = useQuery<UserFreeTrialStatus>(userFreeTrialStatusQuery, {
    skip: !userData?.currentUser,
  });

  const hasExistingFreeTrial = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.subscription?.hasFreeTrialPlan === true));
  }, [freeTrialData]);

  // Existing members cannot start a free trial, so any active subscription makes them ineligible.
  const hasActiveMembership = useMemo(() => {
    const households = freeTrialData?.currentUser?.userHouseholds ?? [];
    return households.some((h) => h.household?.pets?.some((p) => p.device?.hasActiveSubscription === true));
  }, [freeTrialData]);

  return useMemo(() => {
    // Hardware-first arm and the free trial are mutually exclusive. A null reason keeps
    // the restriction banner hidden - the trial UI simply disappears for enrolled visitors.
    if (isHardwareFirstEnrolled) {
      return {
        isEligible: false,
        restrictionReason: null,
      };
    }

    // Check if user has previously purchased this product type
    if (previouslyPurchasedCollarProductIds.includes(collarProductId)) {
      return {
        isEligible: false,
        restrictionReason: 'previously_purchased',
      };
    }

    // Check if logged-in user has an active free trial subscription on any product
    if (hasExistingFreeTrial) {
      return {
        isEligible: false,
        restrictionReason: 'previously_purchased',
      };
    }

    // Existing members can't get a trial, so hide it for any logged-in user with an active membership
    if (hasActiveMembership) {
      return {
        isEligible: false,
        restrictionReason: 'active_membership',
      };
    }

    // Check if cart contains any free trial
    const cartItems = Object.values(cart.cartItems);
    const hasFreeTrialInCart = cartItems.some((item) => {
      if (!isBundleCartItem(item)) {
        return false;
      }

      const subscriptionProduct = products.find(
        (p): p is types.ISubscriptionProduct =>
          types.isModuleSubscriptionProduct(p) && p.sku === item.subscriptionLineItem.sku,
      );

      return subscriptionProduct?.freeTrial === true;
    });

    if (hasFreeTrialInCart) {
      return {
        isEligible: false,
        restrictionReason: 'in_cart',
      };
    }

    return {
      isEligible: true,
      restrictionReason: null,
    };
  }, [
    cart.cartItems,
    products,
    previouslyPurchasedCollarProductIds,
    collarProductId,
    hasExistingFreeTrial,
    hasActiveMembership,
    isHardwareFirstEnrolled,
  ]);
}
